/**
 * Default project style configuration.
 *
 * Exports:
 *  - COLORS_TO_DERIVE       — solo color keys (in DEFAULT_STYLE.colors) that get full variant derivation
 *  - DERIVE_CONTEXT         — render-context identifiers: MODEL / PRODUCT / UI
 *  - DERIVE_VARIANTS        — per-context variant spec lists (suffix / type / params)
 *  - COLOR_DERIVE_CONTEXTS  — registry: which contexts each color family participates in
 *  - DEFAULT_STYLE          — canonical defaults for all style token families
 */

// ── Derivation constants ──────────────────────────────────────────────────────

/** Solo color keys (inside DEFAULT_STYLE.colors) that participate in variant derivation. */
export const COLORS_TO_DERIVE = ['primary', 'secondary', 'accent', 'hero_outlines', 'hero_active']

/**
 * Render-context identifiers.
 *
 * MODEL   — SVG outline shapes on photo-realistic renders (rgba fill — opacity intentional)
 * PRODUCT — SVG compartment paths in PlanView          (solid fill — opacity absolutely forbidden)
 * UI      — buttons, badges, labels, icons             (interactive state variants)
 */
export const DERIVE_CONTEXT = /** @type {const} */ ({
  MODEL:   'model',
  PRODUCT: 'product',
  UI:      'ui',
})

/**
 * Variant specs per render context.
 * `base` (no suffix) is always injected implicitly — not listed here.
 *
 * MODEL variants — rgba fills for SVG outlines on rendered backgrounds.
 * PRODUCT variants — solid tinted fills for dimmed compartments in PlanView.
 * UI variants — interactive state colors for buttons, badges, and labels.
 */
export const DERIVE_VARIANTS = {
  [DERIVE_CONTEXT.MODEL]: [
    { suffix: 'stroke',     type: 'base' },               // alias of base, for SVG stroke
    { suffix: 'fill',       type: 'alpha', alpha: 0.38 }, // rgba 38% — outline fill
    { suffix: 'fill-hover', type: 'alpha', alpha: 0.80 }, // rgba 80% — outline hover fill
  ],
  [DERIVE_CONTEXT.PRODUCT]: [
    { suffix: 'muted',            type: 'tint', percent: 10 },  // solid +10% L — dimmed compartment fill (legend hover)
    { suffix: 'fill-solid',       type: 'tint', percent: 42 },  // solid pale tint — MONO hover fill
    { suffix: 'fill-hover-solid', type: 'tint', percent: 26 },  // solid medium tint — MONO selected fill
  ],
  [DERIVE_CONTEXT.UI]: [
    { suffix: 'hover',      type: 'shade', percent: 10 }, // shade −10% L (darker)
    { suffix: 'pressed',    type: 'tint',  percent: 10 }, // tint  +10% L (lighter)
    { suffix: 'on',         type: 'contrast' },           // max-contrast readable color
    { suffix: 'on-muted',   type: 'muted' },              // muted readable color
  ],
}

/**
 * Registry: which render contexts each color key / family participates in.
 * Controls which variant sets are injected as CSS custom properties.
 *
 * Family → CSS prefix mapping:
 *   status_colors     → --bim-color-status-{key}-{suffix}
 *   rooms_colors      → --bim-color-rooms-{key}-{suffix}
 *   insolation_colors → --bim-color-insolation-{key}-{suffix}
 *   zone_colors       → --bim-color-zone-{key}-{suffix}
 *   type_colors       → --bim-color-type-{key}-{suffix}
 *   compare_colors    → --bim-color-compare-{key}-{suffix}
 *   brightness_colors → --bim-color-brightness-{key}-{suffix}
 *   category_colors   → --bim-color-category-{key}-{suffix}
 */
export const COLOR_DERIVE_CONTEXTS = {
  // ── Solo colors (iterated via COLORS_TO_DERIVE) ──────────────────────────
  primary:           [DERIVE_CONTEXT.MODEL, DERIVE_CONTEXT.UI, DERIVE_CONTEXT.PRODUCT],
  secondary:         [DERIVE_CONTEXT.UI],
  accent:            [DERIVE_CONTEXT.UI],
  hero_outlines:     [DERIVE_CONTEXT.MODEL],
  hero_active:       [DERIVE_CONTEXT.MODEL],
  // ── Color families ───────────────────────────────────────────────────────
  status_colors:     [DERIVE_CONTEXT.MODEL],
  rooms_colors:      [DERIVE_CONTEXT.MODEL],
  insolation_colors: [DERIVE_CONTEXT.MODEL, DERIVE_CONTEXT.PRODUCT],
  zone_colors:       [DERIVE_CONTEXT.PRODUCT],
  type_colors:       [DERIVE_CONTEXT.PRODUCT],
  compare_colors:    [DERIVE_CONTEXT.MODEL, DERIVE_CONTEXT.PRODUCT],
  brightness_colors: [DERIVE_CONTEXT.PRODUCT],
  category_colors:   [DERIVE_CONTEXT.MODEL],
}

export const DEFAULT_STYLE = {
	fonts: {
		url: 'https://fonts.googleapis.com/css2?family=Karla:ital,wght@0,200..800;1,200..800&family=Montserrat:ital,wght@0,100..900;1,100..900&family=Sen:wght@400..800&display=swap',
		primary: 'Karla, sans_serif',
		secondary: 'Montserrat, sans_serif',
		headings: 'Sen, sans_serif',
	},
	colors: {
        primary: "#a55170",

        secondary: "#e3c14d",

        accent: "#e3c14d",

        text:           "#4c4c4c",
        text_muted:     "#666666",
        text_emphasis:  "#000000",
        text_headings:  "#00417f",
        text_labels:    "#666666",
        text_data:      "#00417f",
        table_header:   "#4c4c4c",
        table_values:   "#00417f",
        tooltip:        "#00417f",
        on_tooltip:     "#fff",

        surface_lightest:       "#fff",
        surface_offwhite:       "#f3f3f3",
        surface_light:          "#e6e6e6",
        surface_gray_lightest:  "#dddddd",
        surface_gray_light:     "#cfcfcf",
        surface_gray_base:      "#a8a8a8",
        surface_offblack:       "#333333",
        surface_darkest:        "#000",

        lines_lightest:       "#fff",
        lines_offwhite:       "#f3f3f3",
        lines_light:          "#e6e6e6",
        lines_gray_lightest:  "#dddddd",
        lines_gray_light:     "#cfcfcf",
        lines_gray_base:      "#a8a8a8",
        lines_offblack:       "#333333",
        lines_darkest:        "#000",

        hero_outlines:      "#b7748d",
        hero_active:        "#a55170",
        hero_active_stroke: "#42202d",

        //product_outline: "", //alias of hero_outlines used for hovered compartments stroke in ProductView/PlanView
	},
	status_colors: {
		available:  '#34a500',
		reserved:   '#ff9100',
		sold:       '#a50000',
	},
    rooms_colors: {
        0:  "#9c9c9c",  // no rooms data (grey)
        1:  "#3c5791",  // 1 room
        2:  "#AC40D4",  // 2 rooms
        3:  "#ad962a",  // 3 rooms
        4:  "#698c43",  // 4 rooms
        5:  "#D46C40",  // 5 rooms
        6:  "#E53935",  // 6 rooms
        7:  "#00ACC1",  // 7 rooms
        8:  "#F06292",  // 8 rooms
        9:  "#8D6E63",  // 9 rooms
        10: "#78909C",  // 10 rooms
    },

  // ── Floor plan structural tokens ────────────────────────────────────────────
  // Injected as --bim-plan-{key} on the app root by useThemeVars (no derived variants).
  // Override via panel-styles.json › plan_colors to change per-project at runtime.
  plan_colors: {
    // _room fill buckets (3 tiers based on location, not per type — see _plan-base.scss)
    room_default: '#f7f7f7',   // all interior compartments (default fallback)
    room_outdoor: '#f0f2ee',   // exterior spaces: balcony, terrace, loggia
    room_garden:  '#e8f5e0',   // garden (location: "garden")

    // Structural elements
    wall_construction: '#555555',
    wall_shaft:        '#555555',
    wall_partition:    '#919191',
    stroke_equipment:  '#666666',
    fill_equipment:    '#ffffff',
    stroke_opening:    '#888888',
    fill_opening:      '#ffffff',
    color_tag:         '#444444',
    stroke_railing:    '#999999',
    stroke_stairway:   '#aaaaaa',
    stroke_dashed:     '#888888',
  },

  // ── Color scheme palettes ────────────────────────────────────────────────────
  // Used by applyColorSchemeMutations / svgColorScheme.js for InfoMode plan fills.
  // Injected as --bim-color-{family}-{key} on the app root by useThemeVars.
  // `mono` scheme reuses plan_colors.room_* directly (no JS mutations needed).
  //
  //  zone_colors      → --bim-color-zone-{day|night|neutral|outdoor}   (PRODUCT context)
  //  type_colors      → --bim-color-type-{name}                        (PRODUCT context)
  //  compare_colors   → --bim-color-compare-{p0..p9}                   (MODEL + PRODUCT)
  //  brightness_colors→ --bim-color-brightness-{none|low|high}         (MODEL + PRODUCT)
  zone_colors: {
    day:     '#f5e6b0',   // warm yellow — living, kitchen
    night:   '#c5cfe8',   // cool indigo-blue — bedrooms, bathrooms, toilet
    neutral: '#e0e0e0',   // neutral gray — corridors, wardrobes, laundry, office
    outdoor: '#dcebd6',   // soft green — all exterior/garden locations
  },

  // types — colors per compartment type: product.compartments[].name_cad ('id' prefix dropped:  "idLivingRoomWithAnnex" → "living_room_with_annex")
  type_colors: {
    local: "#DCC69B",
    living_room: "#ebe2ce",
    living_room_with_annex: "#ebe2ce",
    living_room_with_annex_and_bedroom: "#ebe2ce",
    living_room_with_bedroom: "#ebe2ce",
    kitchen: "#EAB6A0",
    kitchen_annex: "#EAC3A0",
    pantry: "#",
    wardrobe: "#D0D0D0",
    bedroom_annex: "#B9C5E0",
    bedroom: "#ced7eb",
    mezzanine: "#A6B4D3",
    office: "#A1AFCE",
    dining_room: "#D5BD8D",
    hobby: "#A1AFCE",
    atelier: "#A1AFCE",
    gym: "#A1AFCE",
    toilet: "#d9ebf3",
    bathroom: "#d9ebf3",
    shower: "#d9ebf3",
    laundry_room: "#B9D4E0",
    sauna: "#B9D4E0",
    utility_room: "#D0D0D0",
    storage_room: "#D0D0D0",
    corridor_with_annex: "#e6e6e6",
    corridor: "#e6e6e6",
    staircase: "#e6e6e6",
    vestibule: "#e6e6e6",
    attic: "#e6e6e6",
    attic_nonutility: "#D0D0D0",
    orangery: "#EAE0B2",
    winter_garden: "#EAE0B2",
    balcony: "#eeeeee",
    loggia: "#B6B6B6",
    terrace: "#B6B6B6",
    garden: "#eaf3d7",
    front_garden: "#D3D8CF",
    garage: "#D0D0D0",
    parking_space: "#D0D0D0",
  },

  // compare — blue gradient (smallest) → white (avg) → green gradient (largest), 10 decile buckets
  compare_colors: {
    p0: '#3d7ab8',   // 0–10th percentile (smallest) — deep blue
    p1: '#6496c4',   // 10–20th — medium-dark blue
    p2: '#8fb3d9',   // 20–30th — medium blue
    p3: '#b9d1ea',   // 30–40th — light blue
    p4: '#e2eef6',   // 40–50th — very light blue (slightly below avg)
    p5: '#e5f3e2',   // 50–60th — very light green (slightly above avg)
    p6: '#b1d9a9',   // 60–70th — light green
    p7: '#86c97e',   // 70–80th — medium green
    p8: '#5cba5a',   // 80–90th — medium-dark green
    p9: '#3aaa47',   // 90–100th (largest) — deep green
  },
  // brightness — three-segment scale:
  // ratio = (window_area / area_original) * 8  (1.0 = legal minimum = 1/8 floor area)
  // no windows (window_area = 0) → neutral gray (--bim-color-brightness-none)
  // ratio < 1.0 → full blue (below legal minimum)
  // ratio 1.0 → 3.0 → linear white → yellow
  // ratio > 3.0 → full yellow (very bright)
  brightness_colors: {
    none: '#d8d8d8',   // gray — no windows (room uncoloured / neutral)
    low:  '#8fb3d9',   // blue — below legal minimum (ratio < 1.0)
    high: '#f5d642',   // yellow — 3× legal minimum (very bright)
  },

  // ── Insolation palette ──────────────────────────────────────────────────────
  // Shared between InsolationMode fills, TiersView/ModelView apartment outlines,
  // and future brightness color scheme.
  // Injected as --bim-color-insolation-{0..10} on the app root.
  // Key 0 = no data / no windows. Keys 1–10 = 1h to 10h (10 = 10h+, max for 7–17 window).
  insolation_colors: {
    null: '#d8d8d8',   // no data (sun_light_ranges: null — brak pomiaru)
    0: '#b0c4ef',   // 0h słońca (windows exist but zero direct sun)
    1: '#b4a7e1',   // 1h
    2: '#bf9dd9',   // 2h
    3: '#e8a6a6',   // 3h — crosses from cool to warm
    4: '#e8a6b1',   // 4h
    5: '#f6a6a9',   // 5h
    6: '#ffb0a6',   // 6h
    7: '#ffc3a6',   // 7h
    8: '#ffd5a6',   // 8h
    9: '#ffe8a6',   // 9h
    10: '#ffffae'   // 10h+
  },

  // ── Product category palette ─────────────────────────────────────────────
  // Used by CATEGORY colorMode in TiersView/ModelView apartment outlines.
  // Injected as --bim-color-category-{key} with MODEL-context variants
  // (-fill rgba 0.38, -fill-hover rgba 0.67, -stroke) via COLOR_DERIVE_CONTEXTS.
  // Keys match PRODUCT_CATEGORY values from productCategories.js.
  // Override via panel-styles.json › category_colors.
  category_colors: {
    // Generic fallbacks
    product:              '#7a8fa6',   // slate-blue (generic)
    real_estate:          '#7a8fa6',   // alias of product
    unit:                 '#7a8fa6',   // alias of product
    // Residential — apartments
    apartment_standard:   '#4a90d9',   // medium blue
    apartment_premium:    '#7b68c8',   // indigo-violet
    apartment_luxury:     '#9b59b6',   // deep violet
    studio:               '#5dade2',   // sky blue
    // Residential — houses
    detached:             '#27ae60',   // grass green
    semi_detached:        '#58d68d',   // light green
    townhouse:            '#82e0aa',   // pale green
    // Commercial
    commercial:           '#e67e22',   // orange
    store:                '#f39c12',   // amber
    // Hospitality
    hotel_room:           '#e74c3c',   // warm red
    hotel_suite:          '#c0392b',   // deep red
  },

  // ── Sections padding-top to accommodate websites sticky nav bars ────────────────────────────────────────────
	section_padding: {
		nav_bar_desktop: '',
		nav_bar_mobile: '',
		addend: 0,
		fallback: '2em',
	},
}





